import {Component, Inject, OnInit} from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { BaseModalComponent } from 'app/shared/base-modal/base-modal.component';
import {
  CreateGroupRequest, DisplayedProduct, emptyOverride, Group,
  GroupOverride,
  MenuSection, NameTranslationType,
  OverrideInstruction,
  OverridePosition,
  OverridePositionRequest, PositionTypeEnum
} from 'app/models/menu.model';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { v4 as uuidv4 } from 'uuid';
import { UntilDestroy } from '@ngneat/until-destroy';
import { MenuOverrideService } from 'app/services/menu-override.service';
import { Organization } from 'app/models/organization.model';
import { MenuService } from 'app/services/menu.service';
import {
  SelectProductsModalComponent,
  SelectProductsModalComponentDataType
} from "app/pages/menu/shared/select-products-modal/select-products-modal.component";

export interface EditGroupDialogData {
  group?: Group;
  groupsMap?: Map<string, Group>;
  originalGroup?: Group;
  menuSections: MenuSection[];
  menuSectionId: string;
  organizationId: string;
  organization: Organization;
  overrideGroupPosition?: OverridePosition;
  overrideGroupStructurePosition?: OverridePosition;
  allowOverrides: boolean;
  products: Map<string, DisplayedProduct>;
}

@UntilDestroy()
@Component({
  selector: 'app-edit-group-modal',
  templateUrl: './edit-group-modal.component.html',
  styleUrls: ['./edit-group-modal.component.scss']
})
export class EditGroupModalComponent extends BaseModalComponent implements OnInit {
  isEditMode: boolean = false;
  form: FormGroup;
  selectedGroupImgSrc;
  overrideGroupImgSrc;

  groupsMap?: Map<string, Group>;
  groups: Group[];

  originalGroup: Group;
  initValues = {};
  selectedMenuSections = {};

  groupName: string;
  groupDescription: string;
  tags: string[];
  originalTags: string[];
  overrideGroupPosition?: OverridePosition;
  overrideGroupStructurePosition?: OverridePosition;
  disableSubmit: boolean;
  newTags: string[] = [];
  newTagIds: string[] = [];
  parentId: string;
  originalParentId?: string;
  hasVipDish: boolean = false;

  changesInformationMessage = '';

  constructor(
    public dialogRef: MatDialogRef<EditGroupModalComponent>,
    private translate: TranslateService,
    public dialog: MatDialog,
    private menuService: MenuService,
    @Inject(MAT_DIALOG_DATA) public data: EditGroupDialogData
  ) {
    super(dialogRef);
  }

  ngOnInit(): void {
    this.groupsMap = this.data.groupsMap;
    this.originalGroup = this.data.originalGroup;
    this.parentId = this.data.group?.groupStructure?.parentId;

    this.groups = [...this.data.groupsMap.values()];

    const originalParentGroup = this.originalGroup ? this.groupsMap.get(this.originalGroup.groupStructure.parentId) : null;
    this.originalParentId = originalParentGroup?.externalId;

    this.isEditMode = !!this.data.group;
    this.groupName = '';
    this.groupDescription = '';
    this.selectedGroupImgSrc = '';
    if (this.data) {
      this.groupName = this.data.group?.name;
      this.groupDescription = this.data.group?.description ? this.data.group?.description : '';
      this.selectedGroupImgSrc = this.data.group?.externalPictureUrl ? this.data.group?.externalPictureUrl : '';
    }

    if (this.data.overrideGroupPosition) {
      this.overrideGroupPosition = this.data.overrideGroupPosition;
      if (this.overrideGroupPosition) {
        const groupOverride = JSON.parse(this.overrideGroupPosition.override) as GroupOverride;
        if (groupOverride.externalPictureUrl) {
          this.overrideGroupImgSrc = groupOverride.externalPictureUrl;
        }
      }
    }

    if (this.data.overrideGroupStructurePosition){
      this.overrideGroupStructurePosition = this.data.overrideGroupStructurePosition;
    }

    this.tags = [];
    this.newTags = [];
    this.newTagIds = [];
    this.originalTags = [];
    this.hasVipDish = false;
    if (this.data.originalGroup) {
      this.originalTags = this.data.originalGroup.tags;
    }
    if (this.data.group) {
      this.tags = this.data.group.tags;
      this.newTags = this.data.group.tags;
      this.newTagIds = this.data.group.tagIds ?? [];
      this.hasVipDish = this.data.group.hasVipDish ?? false;
    }

    this.initValues['parentId'] = this.parentId;
    this.initValues['groupName'] = this.groupName;
    this.initValues['groupDescription'] = this.groupDescription;
    this.initValues['groupPicture'] = this.selectedGroupImgSrc;
    this.initValues['tags'] = this.tags;
    this.initValues['tagIds'] = this.newTagIds ?? [];
    this.initValues['hasVipDish'] = this.hasVipDish ?? false;

    this.form = new FormGroup({
      groupName: new FormControl(this.groupName, [Validators.required]),
      groupPicture: new FormControl(this.selectedGroupImgSrc),
      groupDescription: new FormControl(this.groupDescription),
      parentId: new FormControl(this.parentId),
      hasVipDish: new FormControl(this.hasVipDish),
    });

    this.form.addControl('parentId', new FormControl(this.parentId))

    if (this.data.organization.additionalLanguages) {
      for (const lang of this.data.organization.additionalLanguages) {
        // name translations
        let nameValue = '';
        if (this.data.group?.nameTranslations.hasOwnProperty(lang)) {
          nameValue = this.data.group.nameTranslations[lang];
        }
        this.form.addControl('groupName' + lang, new FormControl(nameValue, []));
        this.initValues['groupName' + lang] = nameValue;

        // description translations
        let descriptionValue = '';
        if (this.data.group?.descriptionTranslations.hasOwnProperty(lang)) {
          descriptionValue = this.data.group.descriptionTranslations[lang];
        }
        this.form.addControl('groupDescription' + lang, new FormControl(descriptionValue, []));
        this.initValues['groupDescription' + lang] = descriptionValue;
      }
    }
  }

  updateChangesInformationMessage() {
    let changes = [];
    if (this.initValues['groupName']?.trim() !== this.form.value['groupName']?.trim()) {
      changes.push(`"${this.translate.instant('MENU.NAME')}"`);
    }
    if (this.initValues['groupDescription'].trim() !== this.form.value['groupDescription'].trim()) {
      changes.push(`"${this.translate.instant('MENU.DESCRIPTION')}"`);
    }
    if (this.initValues['groupPicture'] !== this.form.value['groupPicture']) {
      changes.push(`"${this.translate.instant('MENU.PICTURE')}"`);
    }
    if (this.initValues['tags'].sort().toString() !== this.newTags.sort().toString()) {
      changes.push(`"${this.translate.instant('MENU.TAGS')}"`);
    }
    if (this.initValues['tagIds'].sort().toString() !== this.newTagIds.sort().toString()) {
      changes.push(`"${this.translate.instant('MENU.TAGS')}"`);
    }
    if (this.initValues['hasVipDish']!== this.hasVipDish) {
      changes.push(`"${this.translate.instant('MENU.HAS_VIP_DISH')}"`);
    }
    if (this.data.organization.additionalLanguages) {
      for (const lang of this.data.organization.additionalLanguages) {
        if (this.initValues['groupName' + lang].trim() !== this.form.value['groupName' + lang].trim()) {
          changes.push(`"${this.translate.instant('MENU.NAME')} ${lang}"`);
        }
        if (this.initValues['groupDescription' + lang].trim() !== this.form.value['groupDescription' + lang].trim()) {
          changes.push(`"${this.translate.instant('MENU.DESCRIPTION')} ${lang}"`);
        }
      }
    }

    if ( this.initValues['parentId'] !== this.parentId) {
      changes.push(`"${this.translate.instant('MENU.PARENT_ID')}"`)
    }

    if (changes.length > 0) {
      this.changesInformationMessage = `${this.translate.instant('MENU.CHANGES_FOR_MENUS', {'changes': changes.join(', ')})}`;
    } else {
      this.changesInformationMessage = '';
    }
  }

  async submit() {
    if(!this.data.allowOverrides){
      return;
    }
    if (this.form.valid) {
      if (!this.data.group) {
        this.saveNewGroup();
      } else {
        await this.saveGroupOverrides();
        await this.saveGroupStructureOverrides();
      }
    }
    this.dialogRef.close();
  }

  private async saveGroupOverrides() {
    let groupOverride: GroupOverride;
    let isOverrideExists: boolean;
    const menuSectionId = this.data.menuSectionId;
    // if override position does not exists create a new one
    if (!this.overrideGroupPosition) {
      this.overrideGroupPosition = {
        positionType: PositionTypeEnum.Group,
        positionId: this.data.group.externalId,
        override: emptyOverride,
        menuSectionId: menuSectionId,
      };
      groupOverride = {};
    } else {
      // if override position exists update it anyway no matter has changes or not
      isOverrideExists = true;
      groupOverride = JSON.parse(this.overrideGroupPosition.override) as GroupOverride;
    }

    const overrideInstructions: OverrideInstruction[] = []

    if (this.initValues['groupName'].trim() !== this.form.value['groupName'].trim()) {
      MenuOverrideService.overrideTextFiled(this.data.group, this.originalGroup, 'name', this.form.value.groupName, isOverrideExists, overrideInstructions);
    }

    if (this.initValues['groupDescription'].trim() !== this.form.value['groupDescription'].trim()) {
      MenuOverrideService.overrideTextFiled(this.data.group, this.originalGroup, 'description', this.form.value.groupDescription, isOverrideExists, overrideInstructions);
    }

    if (this.initValues['groupPicture'] !== this.form.value['groupPicture']) {
      MenuOverrideService.overrideTextFiled(this.data.group, this.originalGroup, 'externalPictureUrl', this.form.value.groupPicture, isOverrideExists, overrideInstructions);
    }

    if (this.initValues['tags'].sort().toString() !== this.newTags.sort().toString()) {
      MenuOverrideService.overrideArrayFiled(this.data.group, this.originalGroup, 'tags', this.newTags, isOverrideExists, overrideInstructions);
    }

    if (this.initValues['tagIds'].sort().toString() !== this.newTagIds.sort().toString()) {
      MenuOverrideService.overrideArrayFiled(this.data.group, this.originalGroup, 'tagIds', this.newTagIds, isOverrideExists, overrideInstructions);
    }

    if(this.initValues['hasVipDish'] !== this.form.value['hasVipDish']) {
      MenuOverrideService.overrideBooleanFiled(this.data.group, this.originalGroup, 'hasVipDish', this.form.value.hasVipDish, isOverrideExists, overrideInstructions)
    }

    if (this.data.organization.additionalLanguages) {
      for (const lang of this.data.organization.additionalLanguages) {
        if (this.initValues['groupName' + lang] !== this.form.value['groupName' + lang]) {
          MenuOverrideService.overrideTranslationFiled(this.data.group, this.originalGroup, 'nameTranslations', lang, this.form.value['groupName' + lang], isOverrideExists, groupOverride, overrideInstructions);
        }
        if (this.initValues['groupDescription' + lang] !== this.form.value['groupDescription' + lang]) {
          MenuOverrideService.overrideTranslationFiled(this.data.group, this.originalGroup, 'descriptionTranslations', lang, this.form.value['groupDescription' + lang], isOverrideExists, groupOverride, overrideInstructions);
        }
      }
    }

    if (overrideInstructions.length > 0) {
      const saveForMenuSections = [];
      for (const [key, value] of Object.entries(this.selectedMenuSections)) {
        if (value) {
          saveForMenuSections.push(key);
        }
      }
      const overrideGroup: OverridePositionRequest = {
        ...this.overrideGroupPosition,
        instructions: overrideInstructions,
      };

      await this.menuService.saveOverridePositions(menuSectionId, [overrideGroup], saveForMenuSections);
    }
  }

  private async saveGroupStructureOverrides() {
    if (this.initValues['parentId'] == this.form.value['parentId']) {
      return;
    }

    let isOverrideExists: boolean;
    const menuSectionId = this.data.menuSectionId;
    // if override position does not exists create a new one
    if (!this.overrideGroupStructurePosition) {
      this.overrideGroupStructurePosition = {
        positionType: PositionTypeEnum.GroupStructure,
        positionId: this.data.group.externalId,
        override: emptyOverride,
        menuSectionId: menuSectionId,
      };
    } else {
      isOverrideExists = true;
    }

    let overrideInstructionsGroupStructure: OverrideInstruction[] = [];

    MenuOverrideService.overrideTextFiled(this.data.group, this.originalGroup, 'parentId', this.parentId, isOverrideExists, overrideInstructionsGroupStructure, true);

    let overrideGroupStructure: OverridePositionRequest = {
      ...this.overrideGroupStructurePosition,
      instructions: overrideInstructionsGroupStructure,
    };

    const saveForMenuSections = [];
    for (const [key, value] of Object.entries(this.selectedMenuSections)) {
      if (value) {
        saveForMenuSections.push(key);
      }
    }

    await this.menuService.saveOverridePositions(menuSectionId, [overrideGroupStructure], saveForMenuSections);
  }

  private saveNewGroup() {
    const nameTranslations = {};
    const descriptionTranslations = {};
    if (this.data.organization.additionalLanguages) {
      for (const lang of this.data.organization.additionalLanguages) {
        if (this.form.value['groupName' + lang]) {
          nameTranslations[lang] = this.form.value['groupName' + lang];
        }

        if (this.form.value['groupDescription' + lang]) {
          descriptionTranslations[lang] = this.form.value['groupDescription' + lang];
        }
      }
    }

    const group: CreateGroupRequest = {
      externalId: uuidv4(),
      name: this.form.value.groupName.trim(),
      description: this.form.value.groupDescription ? this.form.value.groupDescription.trim() : '',
      nameTranslations: nameTranslations,
      descriptionTranslations: descriptionTranslations,
      tags: this.newTags,
      tagIds: this.newTagIds,
      externalPictureUrl: this.form.value.groupPicture ? this.form.value.groupPicture.trim() : '',
      groupStructure: {
        productsInGroup: []
      },
      menuSectionId: this.data.menuSectionId
    }

    this.menuService.createGroup(group).subscribe();
  }

  onEditModeChangedTags($event: boolean) {
    this.disableSubmit = $event;
  }

  onTagsChanged($event: string[]) {
    this.newTags = $event;
    this.updateChangesInformationMessage();
  }

  onTagIdsChanged($event: string[]) {
    this.newTagIds = $event;
    this.updateChangesInformationMessage();
  }

  onImageChanged($event: string) {
    this.form.get('groupPicture').setValue($event);
    this.updateChangesInformationMessage();
  }

  clearGroupName() {
    this.form.patchValue({
      groupName: this.originalGroup?.name,
    });
  }

  clearGroupDescription() {
    this.form.patchValue({
      groupDescription: this.originalGroup?.description ? this.originalGroup.description : '',
    });
  }

  isGroupNameDifferent() {
    if (this.originalGroup) {
      return this.originalGroup?.name.trim() !== this.form.value.groupName.trim();
    }

    return false;
  }

  isGroupDescriptionDifferent() {
    if (this.originalGroup && this.form.value.groupDescription) {
      if (!this.originalGroup?.description) {
        return this.form.value.groupDescription?.trim().length > 0;
      }
      return this.originalGroup?.description?.trim() !== this.form.value.groupDescription?.trim();
    }

    return false;
  }

  isGroupNameTranslationDifferent(lang: string) {
    let originalValue = (this.originalGroup?.nameTranslations[lang])?? '';
    const changedValue = this.form.value['groupName' + lang]?.trim();
    return originalValue != changedValue;
  }

  isParentDifferent() {
    return this.parentId != this.originalGroup?.groupStructure.parentId;
  }

  clearGroupNameTranslation(lang: string) {
    this.form.patchValue({
      ['groupName' + lang]: this.originalGroup?.nameTranslations[lang] ? this.originalGroup.nameTranslations[lang] : '',
    });
  }

  isGroupDescriptionTranslationDifferent(lang: string) {
    let originalValue = (this.originalGroup?.nameTranslations[lang])?? '';
    const changedValue = this.form.value['groupDescription' + lang]?.trim();
    return originalValue != changedValue;
  }

  clearGroupDescriptionTranslation(lang: string) {
    this.form.patchValue({
      ['groupDescription' + lang]: this.originalGroup?.descriptionTranslations[lang] ? this.originalGroup.descriptionTranslations[lang] : '',
    });
  }

  getOriginalNameTranslation(lang: string) {
    if (this.originalGroup && this.originalGroup?.nameTranslations && this.originalGroup.nameTranslations.hasOwnProperty(lang)) {
      return this.originalGroup.nameTranslations[lang];
    }
  }

  getOriginalDescriptionTranslation(lang: string) {
    if (this.originalGroup && this.originalGroup?.descriptionTranslations && this.originalGroup.descriptionTranslations.hasOwnProperty(lang)) {
      return this.originalGroup.descriptionTranslations[lang];
    }
  }

  onMenuSelectedChanges($event: object) {
    this.selectedMenuSections = $event;
  }

  dropParent() {
    this.parentId = this.originalParentId;
    this.form.value['parentId'] = this.parentId;
  }

  isParentId(externalId: string) {
    return externalId == this.originalParentId;
  }

  // Возвращает список групп для выбора родителей, исключая текущую группы и её дочерние группы.
  getGroupOptions(): Group[] {
    const filteredOptions = this.groups.filter(option => {
      return this.data.group?.externalId != option.externalId && !this.isChildGroup(this.data.group, option);
    });
    return filteredOptions;
  }

  isChildGroup(current: Group, other: Group): boolean {
    if (!other || !other.groupStructure.parentId) {
      return false;
    }
    return other.groupStructure.parentId == current?.externalId ||
      this.isChildGroup(current, this.groupsMap.get(other.groupStructure.parentId));
  }



  editRelatedProducts() {
    const productIds = [];

    this.dialog.open(SelectProductsModalComponent, {
      data: {
        title: this.translate.instant('MENU.DISHES.NEW.SELECT_RELATED_PRODUCTS', {productName: this.data.group.name}),
        productIds: productIds,
      } as SelectProductsModalComponentDataType
    }).afterClosed().subscribe(async res => {
      if (res) {
        const newProductIds = res.data;
        // this.relatedProducts = newProductIds;
      }
    });
    this.updateChangesInformationMessage();
  }
}
